import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tag,
} from "antd";
import axios from "axios";
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, SearchOutlined, SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import utils from "utils";
import dayjs from "dayjs";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import Flex from "../../../../components/shared-components/Flex";
import { useOrderDeliveries } from "../../../../data/hooks/firestore/useOrderDeliveries";
import { SOO_COOL_DELIVERY_LABELS } from "../../../../constants/OrderConstant";
import { COLOR_1, COLOR_2, COLOR_3, COLOR_4 } from "../../../../constants/ChartConstant";
import DonutChart from "../../../../components/shared-components/DonutChart";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";

const { Option } = Select;

const SooCoolDeliveriesReport = ({ orders = [], dateRange, dateRangeText, loading: ordersLoading }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: deliveries, loading: deliveriesLoading } = useOrderDeliveries(dateRange);
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [filteredDeliveriesData, setFilteredDeliveriesData] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [includePending, setIncludePending] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [stats, setStats] = useState({
    onTime: 0,
    late: 0,
    early: 0,
    pending: 0,
    total: 0,
  });
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [syncResults, setSyncResults] = useState(null);
  const [syncResultsModalVisible, setSyncResultsModalVisible] = useState(false);

  useEffect(() => {
    if (deliveries && orders) {
      // Join deliveries with orders and filter for SooCool only
      const sooCoolDeliveriesWithOrders = deliveries
        .map((delivery) => {
          const order = orders.find((o) => o.id === delivery.orderId);
          if (!order || !SOO_COOL_DELIVERY_LABELS.includes(order.deliveryLabel)) {
            return null;
          }
          return {
            ...delivery,
            order,
          };
        })
        .filter(Boolean);

      setDeliveriesData(sooCoolDeliveriesWithOrders);
    }
  }, [deliveries, orders]);

  useEffect(() => {
    let filtered = deliveriesData;

    // Filter by delivery label
    if (selectedLabel !== "all") {
      filtered = filtered.filter((delivery) => delivery.order.deliveryLabel === selectedLabel);
    }

    // Filter by status
    if (selectedStatus !== "all") {
      filtered = filtered.filter((delivery) => delivery.deliveryStatus === selectedStatus);
    }

    // Filter by pending status (only if "all" or "pending" is selected)
    if (!includePending && (selectedStatus === "all" || selectedStatus === "pending")) {
      filtered = filtered.filter((delivery) => delivery.deliveryStatus !== "pending");
    }

    // Filter by search text
    if (searchText) {
      filtered = utils
        .wildCardSearchOnOrder(
          filtered.map((d) => d.order),
          searchText,
        )
        .map((order) => filtered.find((d) => d.order.id === order.id));
    }

    setFilteredDeliveriesData(filtered);

    // Calculate statistics
    const onTime = filtered.filter((d) => d.deliveryStatus === "on_time").length;
    const late = filtered.filter((d) => d.deliveryStatus === "late").length;
    const early = filtered.filter((d) => d.deliveryStatus === "early").length;
    const pending = filtered.filter((d) => d.deliveryStatus === "pending").length;

    setStats({
      onTime,
      late,
      early,
      pending,
      total: filtered.length,
    });
  }, [selectedLabel, selectedStatus, deliveriesData, includePending, searchText]);

  const showDeliveryDetails = (delivery) => {
    setSelectedDelivery(delivery);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setTimeout(() => {
      setSelectedDelivery(null);
    }, 300);
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      on_time: { color: "green", text: t("on_time") },
      late: { color: "red", text: t("late") },
      early: { color: "yellow", text: t("early") },
      pending: { color: "blue", text: t("pending") },
    };

    const config = statusConfig[status] || { color: "default", text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const dropdownMenu = (delivery) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => showDeliveryDetails(delivery)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </a>
      </Menu.Item>
    </Menu>
  );

  const deliveriesColumns = [
    {
      title: t("customer.name"),
      dataIndex: ["order", "farmorderCustomerName"],
      render: (name, delivery) => (
        <button className={"text-button"} onClick={() => showDeliveryDetails(delivery)}>
          <span style={{ display: "block" }}>{name}</span>
        </button>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, ["order", "farmorderCustomerName"]),
    },
    {
      title: t("delivery_date"),
      dataIndex: "farmorderDeliveryDate",
      render: (date) => dayjs(date).format("DD-MM-YYYY"),
      defaultSortOrder: "ascend",
      sorter: (a, b) => utils.antdTableSorter(a, b, "farmorderDeliveryDate"),
    },
    {
      title: t("order.order_id"),
      dataIndex: ["order", "farmorderId"],
      sorter: (a, b) => utils.antdTableSorter(a, b, ["order", "farmorderId"]),
    },
    {
      title: t("delivery_label"),
      dataIndex: ["order", "deliveryLabel"],
      sorter: (a, b) => utils.antdTableSorter(a, b, ["order", "deliveryLabel"]),
    },
    {
      title: t("status"),
      dataIndex: "deliveryStatus",
      render: (status) => getStatusTag(status),
      sorter: (a, b) => utils.antdTableSorter(a, b, "deliveryStatus"),
    },
    {
      title: t("delivery_time"),
      render: (_, delivery) => {
        if (!delivery.order.deliveredAt) return "-";

        const deliveryTime = dayjs.unix(delivery.order.deliveredAt).format("HH:mm");
        const minutes = delivery.minutesDeviation;

        if (minutes === null || minutes === undefined || minutes === 0) {
          return deliveryTime;
        }

        const sign = minutes > 0 ? "+" : "";
        return `${deliveryTime} (${sign}${minutes} min)`;
      },
      sorter: (a, b) => (a.order.deliveredAt || 0) - (b.order.deliveredAt || 0),
    },
    {
      title: "",
      render: (_, delivery) => (
        <div
          className="text-right"
          style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "3px" }}
        >
          <EllipsisDropdown menu={dropdownMenu(delivery)} />
        </div>
      ),
    },
  ];

  const getPercentage = (value) => {
    return stats.total > 0 ? ((value / stats.total) * 100).toFixed(1) : 0;
  };

  // Determine if pending checkbox should be disabled
  const isPendingCheckboxDisabled = selectedStatus !== "all" && selectedStatus !== "pending";

  // Determine chart data based on selected status
  const getChartData = () => {
    if (selectedStatus === "all") {
      return {
        series: includePending
          ? [stats.onTime, stats.late, stats.early, stats.pending]
          : [stats.onTime, stats.late, stats.early],
        labels: includePending
          ? [t("on_time"), t("late"), t("early"), t("pending")]
          : [t("on_time"), t("late"), t("early")],
        colors: includePending ? [COLOR_2, COLOR_3, COLOR_4, COLOR_1] : [COLOR_2, COLOR_3, COLOR_4],
      };
    } else {
      // Single status selected - show only that status
      const statusMap = {
        on_time: { value: stats.onTime, label: t("on_time"), color: COLOR_2 },
        late: { value: stats.late, label: t("late"), color: COLOR_3 },
        early: { value: stats.early, label: t("early"), color: COLOR_4 },
        pending: { value: stats.pending, label: t("pending"), color: COLOR_1 },
      };

      const selectedStatusData = statusMap[selectedStatus];
      return {
        series: [selectedStatusData.value],
        labels: [selectedStatusData.label],
        colors: [selectedStatusData.color],
      };
    }
  };

  const chartData = getChartData();

  const handleSyncPendingOrders = async () => {
    setSyncModalVisible(false);
    setSyncing(true);
    setSyncProgress({ current: 0, total: 0 });
    setSyncResults(null);

    try {
      // Get all pending deliveries without any filters
      const pendingDeliveries = deliveriesData.filter((d) => d.deliveryStatus === "pending");

      if (pendingDeliveries.length === 0) {
        message.info(t("no_pending_orders_to_sync"));
        setSyncing(false);
        return;
      }

      setSyncProgress({ current: 0, total: pendingDeliveries.length });

      // Get the auth token
      const token = await user.getIdToken();

      // Prepare the order IDs to sync
      const orderIds = pendingDeliveries.map((d) => d.order.id);

      // Call the backend endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/soocool/sync-pending-orders`,
        { orderIds },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      const results = response.data;
      setSyncResults(results);
      setSyncResultsModalVisible(true);

      // Refresh the deliveries data
      // The useOrderDeliveries hook should automatically refresh
    } catch (error) {
      console.error("Error syncing orders:", error);
      message.error(t("deliveries_report_sync_failed"));
    } finally {
      setSyncing(false);
    }
  };

  const showSyncConfirmation = () => {
    setSyncModalVisible(true);
  };

  const hasPendingOrders = stats.pending > 0;

  return (
    <>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <span className="text-muted">{dateRangeText}</span>
        </div>

        <div
          style={{
            marginBottom: 16,
            display: "flex",
            gap: 16,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <Input
              placeholder={t("search")}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 200 }}
            />

            <Select
              value={selectedLabel}
              onChange={setSelectedLabel}
              style={{ width: 100 }}
              placeholder={t("select_delivery_label")}
            >
              <Option value="all">{t("all_labels")}</Option>
              {SOO_COOL_DELIVERY_LABELS.map((deliveryLabel) => (
                <Option key={deliveryLabel} value={deliveryLabel}>
                  {deliveryLabel}
                </Option>
              ))}
            </Select>

            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              style={{ width: 120 }}
              placeholder={t("select_status")}
            >
              <Option value="all">{t("all_statuses")}</Option>
              <Option value="on_time">{t("on_time")}</Option>
              <Option value="late">{t("late")}</Option>
              <Option value="early">{t("early")}</Option>
              <Option value="pending">{t("pending")}</Option>
            </Select>

            <Checkbox
              checked={includePending}
              onChange={(e) => setIncludePending(e.target.checked)}
              disabled={isPendingCheckboxDisabled}
            >
              {t("include_pending")}
            </Checkbox>
          </div>
          <div>
            {hasPendingOrders && (
              <Button
                type="default"
                icon={<SyncOutlined spin={syncing} />}
                onClick={showSyncConfirmation}
                disabled={syncing}
                loading={syncing}
              >
                {syncing ? t("syncing") : t("sync_pending_orders")}
              </Button>
            )}
          </div>
        </div>

        {stats.total > 0 ? (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={24} style={{ display: "column", gap: 40 }}>
              <DonutChart series={chartData.series} labels={chartData.labels} colors={chartData.colors} height={300} />
              <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: 20 }}>
                <div style={{ display: "flex", justifyContent: "center", maxWidth: 700, gap: 8 }}>
                  <Card size="small">
                    <Statistic
                      title={t("total_deliveries")}
                      value={stats.total}
                      suffix={`(100%)`}
                      valueStyle={{ fontSize: 16, minWidth: 100 }}
                    />
                  </Card>
                  {(selectedStatus === "all" || selectedStatus === "on_time") && (
                    <Card size="small">
                      <Statistic
                        title={t("on_time")}
                        value={stats.onTime}
                        suffix={`(${getPercentage(stats.onTime)}%)`}
                        valueStyle={{ color: COLOR_2, fontSize: 16, minWidth: 100 }}
                      />
                    </Card>
                  )}
                  {(selectedStatus === "all" || selectedStatus === "late") && (
                    <Card size="small">
                      <Statistic
                        title={t("late")}
                        value={stats.late}
                        suffix={`(${getPercentage(stats.late)}%)`}
                        valueStyle={{ color: COLOR_3, fontSize: 16, minWidth: 100 }}
                      />
                    </Card>
                  )}
                  {(selectedStatus === "all" || selectedStatus === "early") && (
                    <Card size="small">
                      <Statistic
                        title={t("early")}
                        value={stats.early}
                        suffix={`(${getPercentage(stats.early)}%)`}
                        valueStyle={{ color: COLOR_4, fontSize: 16, minWidth: 100 }}
                      />
                    </Card>
                  )}

                  {(selectedStatus === "all" || selectedStatus === "pending") && includePending && (
                    <Card size="small">
                      <Statistic
                        title={t("pending")}
                        value={stats.pending}
                        suffix={`(${getPercentage(stats.pending)}%)`}
                        valueStyle={{ color: COLOR_1, fontSize: 16, minWidth: 100 }}
                      />
                    </Card>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0", marginBottom: 24 }}>
            <span className="text-muted">{t("no_data_available")}</span>
          </div>
        )}

        <div className="table-responsive">
          <Table
            showSorterTooltip={false}
            columns={deliveriesColumns}
            dataSource={filteredDeliveriesData}
            rowKey="orderId"
            loading={ordersLoading || deliveriesLoading}
            pagination={{ showSizeChanger: true }}
            scroll={{ x: 800 }}
          />
        </div>
      </Card>

      <Drawer
        title={selectedDelivery ? `${t("delivery_details")} - ${selectedDelivery.order.farmorderId}` : ""}
        placement="right"
        onClose={closeDrawer}
        open={drawerVisible}
        width={600}
      >
        {selectedDelivery && (
          <div>
            <Table
              showSorterTooltip={false}
              columns={[
                {
                  title: t("field"),
                  dataIndex: "field",
                  width: "40%",
                },
                {
                  title: t("value"),
                  dataIndex: "value",
                },
              ]}
              dataSource={[
                {
                  key: "id",
                  field: t("id"),
                  value: selectedDelivery.order.id,
                },
                {
                  key: "orderId",
                  field: t("order.order_id"),
                  value: selectedDelivery.order.farmorderId,
                },
                {
                  key: "customer",
                  field: t("customer.name"),
                  value: selectedDelivery.order.farmorderCustomerName,
                },
                {
                  key: "deliveryLabel",
                  field: t("delivery_label"),
                  value: selectedDelivery.order.deliveryLabel,
                },
                {
                  key: "soocoolId",
                  field: t("soo_cool_id"),
                  value: selectedDelivery.order.sooCoolId,
                },
                {
                  key: "soocoolOrderKey",
                  field: t("soo_cool_order_key"),
                  value: selectedDelivery.order.sooCoolOrderKey,
                },
                {
                  key: "soocoolStatus",
                  field: t("soocool_status"),
                  value: selectedDelivery.order.sooCoolStatus ? selectedDelivery.order.sooCoolStatus : "-",
                },
                {
                  key: "soocoolTrackAndTrace",
                  field: t("soocool_track_and_trace"),
                  value: selectedDelivery.order.trackAndTraceLink ? (
                    <a href={selectedDelivery.order.trackAndTraceLink} target="_blank">
                      {selectedDelivery.order.trackAndTraceLink}
                    </a>
                  ) : (
                    "-"
                  ),
                },
                {
                  key: "timeWindow",
                  field: t("time_window"),
                  value: `${dayjs.unix(selectedDelivery.deliveryTimeWindowStart).format("DD-MM-YYYY HH:mm")} - ${dayjs
                    .unix(selectedDelivery.deliveryTimeWindowEnd)
                    .format("HH:mm")}`,
                },
                {
                  key: "realisedDeliveryTimeDate",
                  field: t("realised_delivery_time"),
                  value: selectedDelivery.order.deliveredAt
                    ? `${dayjs.unix(selectedDelivery.order.deliveredAt).format("DD-MM-YYYY HH:mm")}`
                    : "-",
                },
                {
                  key: "status",
                  field: t("status"),
                  value: getStatusTag(selectedDelivery.deliveryStatus),
                },
                {
                  key: "deviation",
                  field: t("deviation"),
                  value:
                    selectedDelivery.minutesDeviation !== null && selectedDelivery.minutesDeviation !== undefined
                      ? `${selectedDelivery.minutesDeviation > 0 ? "+" : ""}${selectedDelivery.minutesDeviation} ${t(
                          "minutes",
                        )}`
                      : "-",
                },
              ]}
              pagination={false}
              bordered
              size="small"
            />
          </div>
        )}
      </Drawer>

      <Modal
        title={t("confirm_sync_pending_orders")}
        open={syncModalVisible}
        onOk={handleSyncPendingOrders}
        onCancel={() => setSyncModalVisible(false)}
        okText={t("sync")}
        cancelText={t("cancel")}
      >
        <p>{t("sync_pending_orders_confirmation", { count: stats.pending })}</p>
        <p>{t("sync_pending_orders_warning")}</p>
      </Modal>

      {/* Loading Modal */}
      <Modal title={t("syncing_orders")} open={syncing} footer={null} closable={false} centered>
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 20 }}>
            <p>{t("syncing_pending_orders_please_wait")}</p>
            <p className="text-muted">{t("syncing_orders_count", { count: syncProgress.total })}</p>
          </div>
        </div>
      </Modal>

      {/* Results Modal */}
      <Modal
        title={t("sync_results")}
        open={syncResultsModalVisible}
        onOk={() => setSyncResultsModalVisible(false)}
        onCancel={() => setSyncResultsModalVisible(false)}
        width={800}
        okText={t("close")}
        cancelButtonProps={{ style: { display: "none" } }}
      >
        {syncResults && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title={t("total")} value={syncResults.total} />
                </Col>
                <Col span={6}>
                  <Statistic
                    title={t("updated")}
                    value={syncResults.updated}
                    valueStyle={{ color: "#3f8600" }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic title={t("unchanged")} value={syncResults.unchanged} />
                </Col>
                <Col span={6}>
                  <Statistic
                    title={t("errors")}
                    value={syncResults.errors}
                    valueStyle={{ color: "#cf1322" }}
                    prefix={syncResults.errors > 0 ? <CloseCircleOutlined /> : null}
                  />
                </Col>
              </Row>
            </div>

            <Table
              columns={[
                {
                  title: t("order.order_id"),
                  dataIndex: "farmorderId",
                  width: 150,
                },
                {
                  title: t("old_status"),
                  dataIndex: "oldStatus",
                  render: (status) => status || "-",
                },
                {
                  title: t("new_status"),
                  dataIndex: "newStatus",
                  render: (status) => status || "-",
                },
                {
                  title: t("result"),
                  dataIndex: "changed",
                  render: (changed, record) => {
                    if (record.error) {
                      return <Tag color="red">{t("error")}</Tag>;
                    }
                    if (changed) {
                      return <Tag color="green">{t("updated")}</Tag>;
                    }
                    return <Tag color="default">{t("unchanged")}</Tag>;
                  },
                },
                {
                  title: t("error_message"),
                  dataIndex: "error",
                  render: (error) => error || "-",
                  ellipsis: true,
                },
              ]}
              dataSource={syncResults.details}
              rowKey={(record) => record.orderId || record.farmorderId}
              pagination={{ pageSize: 10 }}
              size="small"
              scroll={{ y: 400 }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default SooCoolDeliveriesReport;
